/**
 * useSearch hook (P67.2, #486).
 *
 * Wraps GET /v1/search via React Query. The page-level SearchView
 * calls this with the query string from the URL; the topnav input
 * does NOT call this -- it just navigates to /search?q=... so the
 * SearchView renders the actual result list. Live typeahead is a
 * separate UX pass.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";

export interface SearchHit {
  kind: string;
  id: string;
  display_name: string;
  snippet: string;
  score: number;
  surface_url: string;
  /**
   * True iff `surface_url` is an external URI (e.g. SharePoint /
   * Confluence). Rendered via `<a target="_blank">` instead of
   * react-router `<Link>`. Backend computes this; frontend doesn't
   * sniff URLs.
   */
  external: boolean;
}

export interface SearchResponse {
  results: SearchHit[];
  truncated: boolean;
  query: string;
  /**
   * Modules whose results were withheld because this viewer cannot open
   * them. Present so a scoped answer is never shown as the whole
   * answer -- search used to 403 for every position, and after opening
   * it up the opposite failure would be worse: three kinds of eleven,
   * silently, reading as "that is all there is".
   */
  withheld_modules?: string[];
}

export interface SearchOptions {
  /** Whitespace-trimmed query string. Empty disables the query. */
  q: string;
  /** Optional comma-separated kinds filter. */
  kinds?: string;
  /** Max results returned (server clamps to [1, 100]). */
  limit?: number;
  /** Trigram score floor (default 0.15 server-side). */
  minScore?: number;
}

export function useSearch({ q, kinds, limit, minScore }: SearchOptions) {
  const trimmed = q.trim();
  return useQuery<SearchResponse>({
    queryKey: ["search", trimmed, kinds ?? "", limit ?? 20, minScore ?? 0.15],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("q", trimmed);
      if (kinds) params.set("kinds", kinds);
      if (limit !== undefined) params.set("limit", String(limit));
      if (minScore !== undefined) params.set("min_score", String(minScore));
      return await api.get<SearchResponse>(`/v1/search?${params.toString()}`);
    },
    enabled: trimmed.length > 0,
    staleTime: 30_000,
  });
}
